/**
 * TanStack Query hooks for the Emergency module.
 *
 * Same two conventions as features/laboratory/hooks.ts:
 *  * branch-scoped hooks append `branchVersion` so a branch switch re-keys them;
 *  * every mutation that touches an encounter invalidates THAT encounter's
 *    workspace and the board — never "everything".
 *
 * The board is a live work surface: it refetches every 30 s (and on focus),
 * because a minute-old door-to-doctor clock is a wrong clock.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { keys, tenantKey } from '@/lib/queryKeys';
import * as er from './api';

export function invalidateEncounter(qc: QueryClient, id?: number) {
  qc.invalidateQueries({ queryKey: keys.emergency.board() });
  qc.invalidateQueries({ queryKey: keys.emergency.board(12) });
  qc.invalidateQueries({ queryKey: keys.emergency.encounters() });
  if (id) qc.invalidateQueries({ queryKey: keys.emergency.encounter(id) });
}

export function useErMeta() {
  const { branchVersion } = useAuth();
  return useQuery({ queryKey: [...keys.emergency.meta(), branchVersion], queryFn: er.fetchMeta, staleTime: 60_000 });
}

export function useBoard(recentHours = 0) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.emergency.board(recentHours), branchVersion],
    queryFn: () => er.fetchBoard(recentHours),
    // Faster while a doctor page is waiting for an answer, so a decline
    // reaches the ED within seconds rather than half a minute.
    refetchInterval: (q) => (q.state.data?.rows.some((r) => r.alert_status === 'pending')
      ? pollMs(q.state.data.settings.doctor_alert_poll_seconds) : 30_000),
    // Keep polling when this window is behind another one — a decline must
    // not wait for someone to click back into the ED screen.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
}

// Admin → Emergency Dept Customizations → "Check for doctor pages every
// (seconds)" (er_settings.doctor_alert_poll_seconds, 5–60, default 10).
const DEFAULT_POLL_SECONDS = 10;
function pollMs(seconds: number | null | undefined) {
  const s = Number(seconds);
  return (Number.isFinite(s) && s >= 5 ? Math.min(s, 60) : DEFAULT_POLL_SECONDS) * 1000;
}

/**
 * The signed-in user's live ED pages, for the popup mounted in App.tsx. Polls
 * at the branch's configured rate, which every response carries (so an Admin
 * change applies on the next check), and only for users who can act on one
 * (er:clinical) — everyone else never makes the request.
 */
export function useMyAlerts(enabled: boolean) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.emergency.myAlerts(), branchVersion],
    queryFn: er.fetchMyAlerts,
    enabled,
    refetchInterval: (q) => pollMs(q.state.data?.poll_seconds),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
}

export function useAnswerAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { alertId: number; encounterId: number; accept: boolean; reason?: string }) =>
      v.accept ? er.acceptAlert(v.alertId) : er.declineAlert(v.alertId, v.reason).then((r) => ({ ...r, held_consultation: null })),
    onSettled: (_r, _e, v) => {
      qc.invalidateQueries({ queryKey: keys.emergency.myAlerts() });
      // An Accept may have put an OPD consultation on hold.
      qc.invalidateQueries({ queryKey: keys.queue.today() });
      invalidateEncounter(qc, v.encounterId);
    },
  });
}

export function useWorkspace(id: number | null) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.emergency.encounter(id ?? 0), branchVersion],
    queryFn: () => er.fetchWorkspace(id as number),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.doctor_alert?.status === 'pending'
      ? pollMs(q.state.data.settings.doctor_alert_poll_seconds) : 60_000),
    refetchIntervalInBackground: true,
  });
}

export function useEncounterList(params: Parameters<typeof er.fetchEncounters>[0]) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.emergency.encounters(params as Record<string, unknown>), branchVersion],
    queryFn: () => er.fetchEncounters(params),
  });
}

export function useKpi(from: string, to: string) {
  const { branchVersion } = useAuth();
  return useQuery({
    queryKey: [...keys.emergency.kpi(from, to), branchVersion],
    queryFn: () => er.fetchKpi(from, to),
    enabled: !!from && !!to,
  });
}

export function useMlc(id: number, enabled: boolean) {
  const { branchVersion } = useAuth();
  return useQuery({ queryKey: [...keys.emergency.mlc(id), branchVersion], queryFn: () => er.fetchMlc(id), enabled });
}

export function useWards(enabled = true) {
  const { branchVersion } = useAuth();
  return useQuery({ queryKey: [...keys.emergency.wards(), branchVersion], queryFn: er.fetchWards, enabled });
}

export function useDoctors() {
  return useQuery({ queryKey: tenantKey('er', 'doctors'), queryFn: er.fetchDoctors, staleTime: 5 * 60_000 });
}

export function useSpecialties() {
  return useQuery({ queryKey: tenantKey('er', 'specialties'), queryFn: er.fetchSpecialties, staleTime: 5 * 60_000 });
}

/**
 * A mutation bound to one encounter: runs `fn`, then invalidates that
 * encounter's workspace and the board. Every write in the workspace uses this,
 * so no section can forget to refresh the header clocks or the open items.
 */
export function useEncounterMutation<TVars, TResult>(encounterId: number, fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateEncounter(qc, encounterId),
  });
}

export function useCreateArrival() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: er.createArrival, onSuccess: () => invalidateEncounter(qc) });
}
